/**
 * Lógica pura de devoluciones a proveedor (CC-6: NUNCA usar macros lingui aquí).
 *
 * Una devolución a proveedor (purchase_return) es un picking INVERSO contra una
 * orden de compra ya recibida: retira stock del almacén (stock_move de salida) y
 * registra una capa de valoración NEGATIVA al costo promedio móvil vigente, igual
 * que una entrega normal pero documentada como devolución.
 *
 * Cantidades en ENTEROS (unidades menores, factor 1000), igual que F1–F4.
 */

export type PurchaseReturnState = "draft" | "done";

export const PURCHASE_RETURN_STATES: readonly PurchaseReturnState[] = ["draft", "done"] as const;

export function isPurchaseReturnState(value: string): value is PurchaseReturnState {
  return (PURCHASE_RETURN_STATES as readonly string[]).includes(value);
}

export function coercePurchaseReturnState(value: string | null | undefined): PurchaseReturnState {
  return value && isPurchaseReturnState(value) ? value : "draft";
}

/** ¿Se puede confirmar la devolución? Sólo desde borrador. */
export function canConfirmReturn(state: string | null | undefined): boolean {
  return coercePurchaseReturnState(state) === "draft";
}

/** Línea de devolución (subset relevante para el cálculo). */
export interface ReturnLineLike {
  product_id: string | null;
  /** Cantidad a devolver (unidades menores). */
  qty: number | null;
}

/** Salida planificada de una devolución (producto × cantidad). */
export interface ReturnEffect {
  productId: string;
  /** Cantidad a retirar del on-hand (unidades menores, > 0). */
  qty: number;
}

/**
 * Planifica los efectos de una devolución: una salida por línea con cantidad > 0.
 * Agrega por producto (varias líneas del mismo producto se suman). Ignora líneas
 * sin producto o con cantidad <= 0. Determinista, sin efectos.
 */
export function planReturnEffects(lines: ReturnLineLike[]): ReturnEffect[] {
  const byProduct = new Map<string, number>();
  for (const line of lines) {
    const productId = line.product_id;
    const qty = line.qty ?? 0;
    if (!productId || qty <= 0) {
      continue;
    }
    byProduct.set(productId, (byProduct.get(productId) ?? 0) + qty);
  }
  return [...byProduct.entries()].map(([productId, qty]) => ({ productId, qty }));
}

/** Total de unidades a devolver de una devolución (unidades menores). */
export function totalReturnQty(lines: ReturnLineLike[]): number {
  return lines.reduce((acc, l) => acc + Math.max(l.qty ?? 0, 0), 0);
}
